import React, { useMemo, useState } from 'react';
import { formatPctDelta } from './overviewCompute';
import { buildReferringSites, sortSites } from './overviewSitesCompute';

function OverviewSitesTab({
  referralsCurrent,
  referralsPrior,
  loginsCurrent,
  messagesCurrent,
  studiesCurrent,
  facIds,
  asOfDay,
  windowLabel,
  loading,
}) {
  const [showMsg, setShowMsg] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [showStu, setShowStu] = useState(false);
  const [sortKey, setSortKey] = useState('drop');
  const [openId, setOpenId] = useState(null);

  const rows = useMemo(() => {
    const built = buildReferringSites({
      referralsCurrent,
      referralsPrior,
      loginsCurrent,
      messagesCurrent,
      studiesCurrent,
      hubFacIds: facIds,
      asOfDay,
    });
    return sortSites(built, sortKey);
  }, [
    referralsCurrent, referralsPrior, loginsCurrent, messagesCurrent,
    studiesCurrent, facIds, asOfDay, sortKey,
  ]);

  if (loading) return <div className="overview-loading">Loading referring sites…</div>;

  return (
    <div className="overview-panel">
      <div className="overview-box">
        <h3>
          <span>Referring sites · {windowLabel}</span>
          <span className="overview-sites-controls">
            <label className={`overview-check ${showMsg ? 'on' : ''}`}>
              <input type="checkbox" checked={showMsg} onChange={e => setShowMsg(e.target.checked)} />
              Messages
            </label>
            <label className={`overview-check ${showLog ? 'on' : ''}`}>
              <input type="checkbox" checked={showLog} onChange={e => setShowLog(e.target.checked)} />
              Logins
            </label>
            <label className={`overview-check ${showStu ? 'on' : ''}`}>
              <input type="checkbox" checked={showStu} onChange={e => setShowStu(e.target.checked)} />
              Studies
            </label>
            <label className="overview-sort">
              Sort
              <select value={sortKey} onChange={e => setSortKey(e.target.value)}>
                <option value="drop">Biggest drop vs LY</option>
                <option value="most">Most referrals</option>
                <option value="quiet">Quiet longest</option>
              </select>
            </label>
          </span>
        </h3>

        {rows.length === 0 ? (
          <p className="overview-sub">No referring sites in this window for the scoped facilities.</p>
        ) : (
          <div className="overview-tablewrap">
            <table className="overview-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="num">Referrals</th>
                  <th>Vs LY</th>
                  <th>Last referral</th>
                  <th>Top sender</th>
                  {showMsg && <th className="num">Messages</th>}
                  {showLog && <th className="num">Logins</th>}
                  {showStu && <th className="num">Studies</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(s => {
                  const d = formatPctDelta(s.delta);
                  const open = openId === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        className="overview-site-row"
                        onClick={() => setOpenId(open ? null : s.id)}
                      >
                        <td><b>{s.name}</b></td>
                        <td className="num">{s.ref.toLocaleString()}</td>
                        <td><span className={`overview-delta ${d.kind}`}>{d.text}</span></td>
                        <td>
                          {s.lastDay
                            ? `${s.lastDay}${s.daysSince != null ? ` (${s.daysSince}d)` : ''}`
                            : '—'}
                        </td>
                        <td>{s.topSender}{s.topSenderCount ? ` (${s.topSenderCount})` : ''}</td>
                        {showMsg && <td className="num">{s.messages.toLocaleString()}</td>}
                        {showLog && <td className="num">{s.logins.toLocaleString()}</td>}
                        {showStu && <td className="num">{s.studies.toLocaleString()}</td>}
                        <td>
                          <span className={`overview-status ${s.status}`}>{s.statusLabel}</span>
                        </td>
                      </tr>
                      {open && (
                        <tr className="overview-site-detail">
                          <td colSpan={6 + (showMsg ? 1 : 0) + (showLog ? 1 : 0) + (showStu ? 1 : 0)}>
                            <div className="overview-senders">
                              <strong>Senders at {s.name}</strong>
                              {s.senders.length === 0 ? (
                                <p className="overview-footnote">No sender names on cached referrals.</p>
                              ) : (
                                s.senders.map((p, i) => (
                                  <div key={`${p.name}-${i}`} className="overview-sender">
                                    <b>{p.name}</b>
                                    <small>{p.role || 'Sender'} · {p.count} referral{p.count === 1 ? '' : 's'}</small>
                                  </div>
                                ))
                              )}
                              <p className="overview-footnote">
                                HubSpot outreach actions are not available (no CRM API).
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default OverviewSitesTab;
